import axios from '../utils/index';
import { Pat } from '@/typings/request';

/**
 * Admin-side API for the "Business Admin Jobs" page. Matches Java controller
 * `BusinessAdminJobController` (@RequestMapping("/business-admin-job")).
 *
 * Snowflake IDs travel as strings to avoid JavaScript precision loss.
 */
const businessAdminJobsApi = {
    /** Dropdown options for the "Business Admin" select. */
    async getBusinessAdminOptions(keyword?: string) {
        return axios.get(`/business-admin-job/business-admin-options`, {
            params: keyword ? { keyword } : {},
        });
    },

    /** Dropdown options for the "Branch" select, dependent on a business admin. */
    async getBranchOptions(businessAdminUserId: string) {
        return axios.get(`/business-admin-job/branch-options`, {
            params: { businessAdminUserId },
        });
    },

    /**
     * Dropdown options for the "Tasker" (assignee) select.
     * b2bOnly defaults to true on the server (Add/Edit form → B2B taskers only).
     * Pass b2bOnly=false for the quick-assign on an unassigned AUTO job (any active tasker).
     */
    async getTaskerOptions(keyword?: string, b2bOnly?: boolean) {
        const params: Pat = {};
        if (keyword) params.keyword = keyword;
        if (b2bOnly === false) params.b2bOnly = false;
        return axios.get(`/business-admin-job/tasker-options`, { params });
    },

    /**
     * Let the assigned tasker start a future-dated job before its assembly day. One-way: there is
     * no un-release, so the UI only offers it while the job is still date-blocked.
     */
    async allowEarlyStart(businessAdminJobId: string) {
        return axios.patch(`/business-admin-job/${businessAdminJobId}/early-start`);
    },

    /** Quick-assign a tasker to a job that has none (unassigned AUTO job). Any active tasker. */
    async assignTasker(businessAdminJobId: string, taskerId: string) {
        return axios.patch(`/business-admin-job/${businessAdminJobId}/tasker`, { taskerId });
    },

    /**
     * Taskers who tapped "Request" on this job (their tier window passed) and are awaiting
     * admin approval. Returns [{ taskerId, taskerName, areaCode, mobile, requestedTime }].
     */
    async getAcceptRequests(businessAdminJobId: string) {
        return axios.get(`/business-admin-job/${businessAdminJobId}/accept-requests`);
    },

    /** Approve a request: assign the job to this tasker; auto-rejects the others. */
    async approveAcceptRequest(businessAdminJobId: string, taskerId: string) {
        return axios.post(`/business-admin-job/${businessAdminJobId}/accept-requests/${taskerId}/approve`);
    },

    /** Decline a single tasker's pending request. */
    async declineAcceptRequest(businessAdminJobId: string, taskerId: string) {
        return axios.post(`/business-admin-job/${businessAdminJobId}/accept-requests/${taskerId}/decline`);
    },

    /** Paginated list of business-admin jobs (list page). */
    async getList(params: Pat) {
        return axios.get(`/business-admin-job/list`, { params });
    },

    /**
     * Create a job (stamps source_type = MANUAL on the server; only the external
     * token/jobs/multipart API counts as AUTO).
     *
     * Pass `FormData` with parts `data` (JSON string of the DTO) and optionally
     * `manualAttachmentFile` to use the multipart endpoint that uploads the file to S3.
     * Plain objects are still accepted and go through the JSON endpoint.
     */
    async createJob(data: Pat | FormData) {
        return axios.post(`/business-admin-job`, data, {
            timeout: 120000,
        });
    },

    /** Full detail for a single job (for the View / Edit modals). */
    async getJobDetail(businessAdminJobId: string) {
        return axios.get(`/business-admin-job/detail/${businessAdminJobId}`);
    },

    /** Update an existing job. Accepts FormData (multipart) or plain JSON body. */
    async updateJob(businessAdminJobId: string, data: Pat | FormData) {
        return axios.put(`/business-admin-job/${businessAdminJobId}`, data, {
            timeout: 120000,
        });
    },

    /** Chat-only pause / resume / reschedule without resubmitting the full job form. */
    async updateChatStatus(
        businessAdminJobId: string,
        data: { productStatus: 'PAUSE' | 'RESCHEDULE' | 'INPROGRESS'; scheduledAt?: string }
    ) {
        return axios.patch(`/business-admin-job/${businessAdminJobId}/chat-status`, data);
    },

    /** Soft-delete a job (sets delete_time server-side). */
    async deleteJob(businessAdminJobId: string) {
        return axios.delete(`/business-admin-job/${businessAdminJobId}`);
    },
};

export default businessAdminJobsApi;
